import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Search, Users } from "lucide-react";
import type { ListModality, WorkspaceList } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  createWorkspaceList,
  deleteWorkspaceList,
  listWorkspaceLists,
  updateWorkspaceList,
} from "@/api/lists";

type ModalityFilter = "all" | ListModality;

function formatUpdated(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ListsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [modality, setModality] = useState<ModalityFilter>("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<WorkspaceList | null>(null);
  const [createName, setCreateName] = useState("");
  const [createModality, setCreateModality] = useState<ListModality>("people");
  const [renameName, setRenameName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["workspace-lists", modality],
    queryFn: () =>
      listWorkspaceLists(
        modality === "all" ? undefined : { modality },
      ),
    staleTime: 15_000,
  });

  const lists = useMemo(() => {
    const all = data?.lists ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((l) => l.name.toLowerCase().includes(needle));
  }, [data?.lists, q]);

  const createMutation = useMutation({
    mutationFn: () =>
      createWorkspaceList({ name: createName.trim(), modality: createModality }),
    onSuccess: (list) => {
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      setCreateOpen(false);
      setCreateName("");
      navigate(`/lists/${list.listId}`);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  const renameMutation = useMutation({
    mutationFn: () =>
      updateWorkspaceList(renameTarget!.listId, { name: renameName.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      setRenameTarget(null);
      setRenameName("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (listId: string) => deleteWorkspaceList(listId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspace-lists"] }),
  });

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Lists</h1>
          <p className="text-sm text-muted-foreground">
            Named people and company lists. Save from Find people or Find companies.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
        >
          <Plus className="size-4" />
          Create list
        </button>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form
          className="relative min-w-[200px] flex-1"
          onSubmit={(e) => e.preventDefault()}
        >
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search lists…"
            className="w-full rounded-md border border-border bg-bg py-2 pl-8 pr-3 text-sm"
          />
        </form>
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
          {(
            [
              ["all", "All"],
              ["people", "People"],
              ["companies", "Companies"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setModality(id)}
              className={cn(
                "rounded px-2.5 py-1 text-xs font-medium",
                modality === id
                  ? "bg-brand text-brand-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="mb-3 text-xs text-destructive">{error}</p>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading lists…</p>
      ) : lists.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-6 py-16 text-center">
          <p className="text-sm font-medium text-foreground">No lists yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Create a list, or save selections from People or Companies.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
            >
              Create list
            </button>
            <Link
              to="/people"
              className="rounded-md border border-border px-3 py-1.5 text-xs"
            >
              Find people
            </Link>
            <Link
              to="/companies"
              className="rounded-md border border-border px-3 py-1.5 text-xs"
            >
              Find companies
            </Link>
          </div>
        </div>
      ) : (
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/10 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Modality</th>
                <th className="px-3 py-2 font-semibold">Members</th>
                <th className="px-3 py-2 font-semibold">Updated</th>
                <th className="px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((list) => (
                <tr
                  key={list.listId}
                  className="border-b border-border hover:bg-muted/10"
                >
                  <td className="px-3 py-2.5">
                    <Link
                      to={`/lists/${list.listId}`}
                      className="font-semibold text-brand hover:underline"
                    >
                      {list.name}
                    </Link>
                    {list.description && (
                      <p className="text-[11px] text-muted-foreground">
                        {list.description}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px]">
                      {list.modality === "companies" ? (
                        <Building2 className="size-3" />
                      ) : (
                        <Users className="size-3" />
                      )}
                      {list.modality === "companies" ? "Companies" : "People"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                    {list.memberCount}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {formatUpdated(list.updatedAt)}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <Link
                        to={`/lists/${list.listId}`}
                        className="rounded border border-border px-2 py-1 hover:bg-muted/20"
                      >
                        Open
                      </Link>
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 hover:bg-muted/20"
                        onClick={() => {
                          setRenameTarget(list);
                          setRenameName(list.name);
                        }}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete list “${list.name}”? This cannot be undone.`,
                            )
                          ) {
                            deleteMutation.mutate(list.listId);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <Modal onClose={() => setCreateOpen(false)}>
          <h3 className="text-sm font-semibold">Create list</h3>
          <input
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="List name"
            className="mt-3 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            autoFocus
          />
          <div className="mt-3 flex gap-2">
            {(
              [
                ["people", "People"],
                ["companies", "Companies"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setCreateModality(id)}
                className={cn(
                  "flex-1 rounded-md border px-2 py-2 text-xs font-medium",
                  createModality === id
                    ? "border-brand bg-brand/10 text-foreground"
                    : "border-border text-muted-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="text-xs text-muted-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!createName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </Modal>
      )}

      {renameTarget && (
        <Modal onClose={() => setRenameTarget(null)}>
          <h3 className="text-sm font-semibold">Rename list</h3>
          <input
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            className="mt-3 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            autoFocus
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setRenameTarget(null)}
              className="text-xs text-muted-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!renameName.trim() || renameMutation.isPending}
              onClick={() => renameMutation.mutate()}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Modal({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
